import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  GameModeEnum,
  GameResults,
  GameSlideAnswer,
  IGame,
  LegacyGame,
  User,
} from 'lingo2-models';
import {
  GameLibModule,
  GameStore,
  IGameInfo,
  IGameState,
  SlidesBodyComponent,
  SlidesNavComponent,
} from 'lingo2-game-lib';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { GameService } from '../services/game.service';
import { applyBoardGameCommand, isBoardGameCommandMessage, isReplayMode, nextReplayGeneration } from '../services/board-game-command';
import { CommonModule } from '@angular/common';
import { NoDataComponent } from '../no-data/no-data.component';
import { LessonFinishComponent } from './lesson-finish/lesson-finish.component';
import { VocabularyAddComponent } from './vocabulary-add/vocabulary-add.component';
import { VocabularyFinishComponent } from './vocabulary-finish/vocabulary-finish.component';
import { IconsService } from '../services/icons.service';
import {
  IScreenOptionsFull,
  ScreenOptionsService,
} from '../services/screen-options.service';
import { EmbedAppIcService } from '@onclass/oc-svc/embedapp';
import { computeMaxBottom, resolveFrameHeight } from '../services/content-height';

export interface ISelectedTextData {
  selectedWord: string;
  selectedPosition: ISelectedPosition;
}
interface ISelectedPosition {
  top: number;
  left: number;
}

/*
*/
const getContentHeight = () => {
  const body = document.body;
  if (!body) return 0;

  const elements = Array.from(body.querySelectorAll('*'));
  if (!elements.length) return Math.ceil(body.getBoundingClientRect().height);

  return computeMaxBottom(elements.map((el) => el.getBoundingClientRect()), window.scrollY);
};

/** Честная высота фрейма: содержимое body, поднятое до scrollHeight при переполнении вьюпорта — см. content-height.ts */
const getFrameHeight = () =>
  resolveFrameHeight(getContentHeight(), document.documentElement.scrollHeight, window.innerHeight);

/*
*/
@Component({
  selector: 'app-game',
  standalone: true,
  imports: [
    CommonModule,
    GameLibModule,
    NoDataComponent,
    LessonFinishComponent,
    VocabularyAddComponent,
    VocabularyFinishComponent,
    SlidesBodyComponent,
    SlidesNavComponent,
  ],
  templateUrl: './game.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './game.component.scss',
})
export class GameComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() set lessonId(lessonId: string) {
    if (lessonId) {
      this._lessonId = lessonId;
      this.getGame(!!this.gameService.gameId);
    } else {
      this.error = true;
    }
  }

  public loaded = false;
  public renderInterval: any;
  public slideIndex: number = 0;
  public isFinish = false;
  public isFinished = false;
  public author: User | any = null;
  public svgSetIcon = IconsService.svgsetIconUrl;

  private _game: Partial<IGame> | any = null;
  public gameId = '';
  public _lessonId = '';
  public selectedText: ISelectedTextData = {
    selectedWord: '',
    selectedPosition: {
      top: 0,
      left: 0,
    },
  };
  public answerState: string = '';
  public close = false;
  public error = false;

  private _started_at = new Date();
  private _gameState: IGameState | any = null;
  public gameResults: Partial<GameResults | any> | any = null;
  private _gameResults: GameResults | any = null;

  private gameNav: any;
  private gameContainer: any;
  /** сколько ждём кадр отрисовки, прежде чем отправить высоту и без него */
  private static readonly FRAME_WAIT_MS = 150;

  private resizeTimeout: any;
  private lastSentHeight: number = 0;
  /** признак «библиотека доложила: все слайды загружены» — больше не гейтит отправку, только для отладки/будущего */
  private allSlidesLoaded = false;
  /** страховка: если за 2с после загрузки не ушло ни одного resize — отправляем то, что есть */
  private firstResizeSafetyTimeout: any;
  private mutationObserver: MutationObserver | null = null;
  private trackedImages: HTMLImageElement[] = [];
  private fontsReadyHandled = false;
  private onSlideImageLoad = () => this.debouncedSendResizeMessage(0);

  public screenOptions: Observable<IScreenOptionsFull>;
  public isMobile$: Observable<boolean>;
  public gameOptions: any = null;
  public gameInfoReady = false;

  protected readonly destroyed$ = new Subject<boolean>();

  constructor(
    private gameStore: GameStore,
    private screenOptionsService: ScreenOptionsService,
    private gameService: GameService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    private injector: Injector
  ) {}

  ngOnInit() {
    this.gameService.author$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res) => {
        this.author = res;
      });
    this.screenOptions = this.screenOptionsService.options;
    this.isMobile$ = this.screenOptions.pipe(
      map((options) => !!options.isMobileDevice)
    );
    if (isReplayMode(String(this.gameService.mode)) && this.gameService.sessionId) {
      const sessionId = this.gameService.sessionId;
      // старый пакет читает флаг окна; новый — только свою сессию
      (window as unknown as { __ocGameReplay?: boolean }).__ocGameReplay = true;
      void import('lingo2-game-lib').then((lib) => {
        const set = (lib as { setGameLibReplaySession?: (id: string, enabled: boolean) => void }).setGameLibReplaySession;
        set?.(sessionId, true);
      });
    }
    this.startWatchGame();
    this.listenBoardCommands();
  }

  /** журнал доски после reload iframe — иначе финиш только в памяти */
  private listenBoardCommands(): void {
    const sessionId = this.gameService.sessionId;
    if (!sessionId) return;
    let generation = -1;
    const onMessage = (e: MessageEvent) => {
      if (!isBoardGameCommandMessage(e.data, sessionId) || !this.gameId) return;
      const accepted = nextReplayGeneration(generation, e.data.generation);
      if (accepted === null) return;
      generation = accepted;
      applyBoardGameCommand(this.gameStore, this.gameId, e.data.cmd, e.data.data);
    };
    window.addEventListener('message', onMessage);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('message', onMessage);
      delete (window as unknown as { __ocGameReplay?: boolean }).__ocGameReplay;
      void import('lingo2-game-lib').then((lib) => {
        const set = (lib as { setGameLibReplaySession?: (id: string, enabled: boolean) => void }).setGameLibReplaySession;
        set?.(sessionId, false);
      });
    });
  }

  private startWatchGame() {
    toObservable(this.gameStore.currentGameInfo, { injector: this.injector })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: IGameInfo | null) => {
          this.handleGameInfoChange(res);
        },
        error: (err) => {
          console.error('Error watching game info:', err);
        },
      });
  }

  ngAfterViewInit() {}

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.observer && this.gameContainer) {
      this.observer.unobserve(this.gameContainer);
    }
    this.observer.disconnect();
    if (this.renderInterval) {
      clearInterval(this.renderInterval);
    }
    if (this.firstResizeSafetyTimeout) {
      clearTimeout(this.firstResizeSafetyTimeout);
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    this.trackedImages.forEach((img) => img.removeEventListener('load', this.onSlideImageLoad));
    this.trackedImages = [];
  }

  public set game(game: Partial<IGame>) {
    this._game = game;
    this.gameOptions = game?.options || null;
  }

  public get game() {
    return this._game;
  }

  public getGame(replay = false) {
    if (isReplayMode(String(this.gameService.mode))) {
      this.gameService
        .loadBoardGameData()
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (game) => this.applyLoadedGame(game, 'Replay'),
          error: () => this.applyLoadedGame(new LegacyGame({}), 'Replay'),
        });
      return;
    }
    // на доске один снимок на сессию — иначе у ученика другой shuffle
    if (this.gameService.sessionId && !replay) {
      this.gameService
        .loadBoardGameData()
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (game) => this.applyLoadedGame(game, 'Board'),
          error: () => this.applyLoadedGame(new LegacyGame({}), 'Board'),
        });
      return;
    }

    if (replay) {
      this.gameService
        .getGameById(this.gameService.gameId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (game) => this.applyLoadedGame(game, 'Replay'),
          error: () => this.applyLoadedGame(new LegacyGame({}), 'Replay'),
        });
      return;
    }

    this.gameService
      .prepareGameByLessonId(this._lessonId, GameModeEnum.single)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (game) => this.applyLoadedGame(game, 'New'),
        error: () => this.applyLoadedGame(new LegacyGame({}), 'New'),
      });
  }

  private applyLoadedGame(game: LegacyGame, source: string) {
    if (!game || !game.id) {
      this.error = true;
      this.loaded = true;
      this.gameService.sendMessage(`gamelib::${this._lessonId}::loaded::true`);
      this.cdr.detectChanges();
      return;
    }

    this._game = game;
    this.gameOptions = game?.options ?? null;
    this.gameId = game.id;
    try {
      this.gameStore.setGame(game);
    } catch (err) {
      console.error('[Game-Fe] setGame failed', err);
      this.error = true;
      this.loaded = true;
      this.gameService.sendMessage(`gamelib::${this._lessonId}::loaded::true`);
      this.cdr.detectChanges();
      return;
    }
    this.gameStore.sessionId = this.gameService.sessionId;
    console.debug(
      `✔️ [Game-Fe.${source}] loaded: sessionId=${this.gameStore.sessionId}, gameId=${this.gameId}, lessongId=${this.lessonId}`
    );
    this.gameInfoReady = true;
    this.loaded = true;
    this.cdr.detectChanges();
    this.gameService.sendMessage(`gamelib::${this._lessonId}::loaded::true`);
    EmbedAppIcService.sendToChilds({
      src: 'gamelib',
      data: {
        sessionId: this.gameService.sessionId,
        lessonId: this._lessonId,
        gameId: this.gameId,
        action: 'evt:loaded',
      },
    });
    this.checkIfGameRendered();
    this.scheduleFirstResizeSafety();
  }

  /** страховка от тишины: allSlidesLoaded может не прийти вовсе — тогда без этого таймера resize не уйдёт никогда */
  private scheduleFirstResizeSafety(): void {
    if (this.firstResizeSafetyTimeout) {
      clearTimeout(this.firstResizeSafetyTimeout);
    }
    this.firstResizeSafetyTimeout = setTimeout(() => {
      if (this.lastSentHeight > 0) {
        return;
      }
      // страховка меряет и отправляет сама: её смысл — сработать там, где обычный путь не сработал
      this.emitHeightIfChanged(true);
    }, 2000);
  }

  private slideIndexTracker = 0;

  private handleGameInfoChange(res: IGameInfo | null) {
    if (!res) {
      // Не устанавливаем gameInfoReady в false, если игра уже загружена
      if (this.gameId) {
        return;
      }
      this.gameInfoReady = false;
      this.cdr.detectChanges();
      return;
    }
    // Убеждаемся, что gameInfoReady установлен в true
    if (!this.gameInfoReady) {
      this.gameInfoReady = true;
      this.cdr.detectChanges();
    }
    const gameState = res?.gameState;
    const game = res?.game;
    if (gameState) {
      if (this.slideIndexTracker !== gameState.index) {
        const slideRef = document.querySelector('gamelib-slides-nav');
        if (slideRef) {
          this.slideIndexTracker = gameState.index;
          const activeSlide =
            slideRef.getElementsByClassName('slides-nav-item')[
              this.slideIndexTracker
            ];
          if (activeSlide) {
            activeSlide.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center',
            });
          }
        }
      }
    }

    this.onGameFinish(gameState?.isFinish);
    this.onGameFinished(gameState?.finished);

    this._gameState = { ...gameState };
    if (game && game.results) {
      this._game = game;
      this.gameOptions = game?.options || null;
      this._gameResults = new GameResults(game.results);
    }
    if (gameState && game && game.results) {
      this.updateGameParticipant();
    }

    setTimeout(() => {
      this.sendResizeMessage();
    }, 450);
  }

  protected updateGameParticipant() {
    if (!this._game || !this._gameResults) {
      return;
    }
    const results = this._gameResults;
    const state = this._gameState;

    const _answers: GameSlideAnswer[] = results.answers
      .filter((answer: GameSlideAnswer) => answer.is_answered)
      .map((answer: GameSlideAnswer) => new GameSlideAnswer(answer));

    const stateFinished = results.passProgress === 100 || this.isFinished;
    this.gameResults = {
      ...results,

      answers: _answers,
      slides_count: this._game.slides.length,
      total_points: results.totalPoints,
      max_points: results.maxPoints,
      percent: results.percent,
      progress: results.passProgress,
      index: state.index,
      started_at: this._started_at,
      finished_at: stateFinished ? new Date() : null,
    };

    if (
      !(this._game.options && this._game.options.readOnly) &&
      (!this._gameState.finished || !this.isFinished)
    ) {
      this.sendGameResults();
    }
  }

  protected sendGameResults() {
    // снимок meetings нет в content — PUT participant даст 404
    if (isReplayMode(String(this.gameService.mode)) || this.gameService.mode === GameModeEnum.preview || this.gameService.sessionId) {
      return;
    }

    this.gameService
      .updateParticipant(this._game.id, this.gameResults)
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
  }

  public onClose() {
    this.close = true;
    this.gameService.sendMessage(`gamelib::${this._lessonId}::closed::true`);
  }

  public onSubmitReview() {
    this.gameService.sendMessage(`gamelib::${this._lessonId}::review::true`);
  }

  public onOpenAuthorPage() {
    this.gameService.sendMessage(
      `gamelib::${this._lessonId}::openAuthorPage::true`
    );
  }

  public onTextSelect(e: ISelectedTextData) {
    this.selectedText = e;
    this.close = false;
    this.gameService.sendMessage(
      `gamelib::${this._lessonId}::textselected::${this.selectedText}`
    );
  }

  public onIsAnswerChecked(event: any) {
    this.answerState = String(event);
    this.close = false;
    this.gameService.sendMessage(
      `gamelib::${this._lessonId}::answerstate::${this.answerState}`
    );
  }

  public onAllSlidesLoaded() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }

    // дальше можно пересчитывать по ResizeObserver — но это больше не гейт для отправки,
    // первая честная высота уже могла уйти из checkIfGameRendered()
    this.allSlidesLoaded = true;

    if (!this.gameContainer) {
      this.gameContainer = document.getElementsByClassName('game')[0];
    }
    if (!this.gameNav) {
      this.gameNav = document.getElementsByTagName(
        'gamelib-slides-nav'
      )[0] as Element;
    }

    if (this.gameContainer && this.gameNav) {
      this.observer.unobserve(this.gameContainer as Element);
      this.observer.observe(this.gameContainer as Element);
      this.watchSlideMutations(this.gameContainer as HTMLElement);
    }

    setTimeout(() => {
      if (!this.gameContainer) {
        this.gameContainer = document.getElementsByClassName('game')[0];
      }
      requestAnimationFrame(() => {
        const height = getFrameHeight();
        this.lastSentHeight = 0;
        this.gameService.sendMessage(
          `gamelib::${this._lessonId}::resize::${height}`
        );

        EmbedAppIcService.sendToChilds({
          src: 'gamelib',
          data: { sessionId: this.gameService.sessionId, lessonId: this._lessonId, gameId: this.gameId, action: 'evt:height', val: { height } },
        });

        this.lastSentHeight = height;
      });
    }, 100);
  }

  public onSlideChange(index: number) {
    if (this.slideIndex === index) {
      return;
    }
    this.slideIndex = index;
    this.checkIfGameRendered();
    setTimeout(() => {
      // назад на тот же размер слайда иначе resize не уйдёт
      this.sendResizeMessage(true);
    }, 450);
  }

  observer = new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      this.debouncedSendResizeMessage(entry.contentRect.height);
    });
  });

  checkIfGameRendered() {
    clearInterval(this.renderInterval);
    this.gameNav = document.getElementsByTagName(
      'gamelib-slides-nav'
    )[0] as Element;
    this.gameContainer = document.getElementsByClassName('game')[0];
    if (this.gameContainer && this.gameNav) {
      this.observer.unobserve(this.gameContainer as Element);
      this.observer.observe(this.gameContainer as Element);
      this.watchSlideMutations(this.gameContainer as HTMLElement);
      // первая честная высота сразу после отрисовки — не ждём allSlidesLoaded (причина «тишины»)
      this.sendResizeMessage();
    } else {
      this.renderInterval = setInterval(() => {
        this.checkIfGameRendered();
      }, 200);
    }
  }

  /** пересчёт после подгрузки картинок/шрифтов/вставки текста — без этого высота застывает на первом кадре */
  private watchSlideMutations(container: HTMLElement): void {
    if (!this.mutationObserver) {
      this.mutationObserver = new MutationObserver(() => {
        this.debouncedSendResizeMessage(0);
        this.trackSlideImages(container);
      });
    }
    this.mutationObserver.disconnect();
    this.mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
    this.trackSlideImages(container);

    if (!this.fontsReadyHandled && (document as any).fonts?.ready) {
      this.fontsReadyHandled = true;
      (document as any).fonts.ready
        .then(() => this.debouncedSendResizeMessage(0))
        .catch(() => {});
    }
  }

  private trackSlideImages(container: HTMLElement): void {
    const images = Array.from(container.querySelectorAll('img'));
    images.forEach((img) => {
      if (this.trackedImages.includes(img) || img.complete) {
        return;
      }
      img.addEventListener('load', this.onSlideImageLoad);
      this.trackedImages.push(img);
    });
  }

  private debouncedSendResizeMessage(_height: number) {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      this.measureAndSendHeight(false);
    }, 450);
  }

  /**
   * Замер и отправка БЕЗ ожидания кадра отрисовки.
   *
   * Раньше и отправка, и страховочный таймер уходили внутрь `requestAnimationFrame`. Браузер не
   * выдаёт кадры невидимой вкладке и iframe'у, который не попадает в видимую область, поэтому
   * высота считалась вовремя, но сообщение родителю не уходило: замер на стенде дал 86 секунд
   * тишины при готовом содержимом и уже посчитанной верной высоте 761. Кадр ждём только когда
   * страница действительно видима, и то не дольше `FRAME_WAIT_MS`.
   */
  private measureAndSendHeight(force: boolean): void {
    if (document.hidden) {
      this.emitHeightIfChanged(force);
      return;
    }

    let done = false;
    const run = () => {
      if (done) {
        return;
      }
      done = true;
      this.emitHeightIfChanged(force);
    };

    requestAnimationFrame(run);
    setTimeout(run, GameComponent.FRAME_WAIT_MS);
  }

  private emitHeightIfChanged(force: boolean): void {
    const finalHeight = getFrameHeight();

    if (force || Math.abs(finalHeight - this.lastSentHeight) > 1) {
      this.lastSentHeight = finalHeight;
      this.gameService.sendMessage(
        `gamelib::${this._lessonId}::resize::${finalHeight}`
      );
    }
  }

  private sendResizeMessage(force = false) {
    this.measureAndSendHeight(force);
  }

  public get isLesson(): boolean {
    return !this.isVocabulary;
  }

  public get isVocabulary(): boolean {
    return [
      GameModeEnum.memorizer,
      GameModeEnum.scenario,
      GameModeEnum.test,
    ].includes(this.gameService.mode);
  }

  public onGameFinish(event: any) {
    this.isFinish = event;
    this.gameService.sendMessage(
      `gamelib::${this._lessonId}::isFinish::${this.isFinish}`
    );
    const finishRef = document.querySelector('app-lesson-finish');
    if (finishRef) {
      setTimeout(() => {
        finishRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          this.sendResizeMessage();
        }, 450);
      }, 100);
    } else {
      setTimeout(() => {
        this.sendResizeMessage();
      }, 450);
    }
  }

  public onGameFinished(event: any) {
    this.isFinished = event;
    this.gameService.sendMessage(
      `gamelib::${this._lessonId}::finished::${this.isFinished}`
    );
  }
}
